import { useMemo, useState } from "react"
import { Link, useLocation } from "react-router-dom"
import { useFeature } from "@growthbook/growthbook-react"

import { UserAvatar } from "../../UserAvatar"

import styles from "./styles.module.scss"

import { Icon } from "components/Icon"
import { isClientAuthorizationPage } from "app/AppRouter/utils"
import { Analytics } from "utils/analytics"
import { MyAuthorizationLink } from "app/AppRouter/links"
import { GiftLink } from "components/Header/components/GiftLink"
import { MobileNavigation } from "components/Header"
import { useClientContext } from "modules/ClientAuthorization/common/ClientContext"
import { APP_PAGE_URL, BLOG_URL } from "utils/env-management"

export function MenuMobile({ showDesktop }: { showDesktop?: boolean | null }) {
    const [knowledgeOpen, setKnowledgeOpen] = useState(false)
    const [aboutOpen, setAboutOpen] = useState(false)
    const [{ isVerified, userAccount }] = useClientContext()
    const location = useLocation()
    const { value: experimentClosedProduct } = useFeature<boolean>("closed-product")

    const showClosedMenu = useMemo(() => {
        if (experimentClosedProduct) {
            if (userAccount?.psychologists.length === 0) {
                return false
            }

            if (userAccount?.completed_surveys === 0) {
                return false
            }
        }

        return true
    }, [experimentClosedProduct, userAccount])

    const showSelection = useMemo(() => {
        if (!experimentClosedProduct) {
            return true
        }

        return userAccount?.completed_surveys > 0
    }, [experimentClosedProduct, userAccount])

    return (
        <MobileNavigation.Menu key={location.key} title="alter" highlight showDesktop={showDesktop}>
            <MobileNavigation.Highlight>
                <MobileNavigation.Line />
                {isVerified ? (
                    <>
                        <MobileNavigation.Item>
                            <Link to="/my/userinfo" className={styles.isProfileLink}>
                                <UserAvatar size="XS" isMenu />
                                Профиль
                            </Link>
                        </MobileNavigation.Item>
                        <MobileNavigation.Line />
                        <MobileNavigation.Item>
                            <Link
                                to="/my/alter"
                                onClick={() => {
                                    Analytics.go_to_my_alter()
                                }}
                            >
                                Мой Alter
                            </Link>
                        </MobileNavigation.Item>
                        {showClosedMenu && (
                            <>
                                <MobileNavigation.Line />
                                <MobileNavigation.Item>
                                    <Link
                                        to="/my/sessions"
                                        onClick={() => {
                                            Analytics.go_to_sessions()
                                        }}
                                    >
                                        Сессии с психологом
                                    </Link>
                                </MobileNavigation.Item>
                            </>
                        )}
                        {showSelection && (
                            <>
                                <MobileNavigation.Line />
                                <MobileNavigation.Item>
                                    <Link
                                        to="/my/selections"
                                        onClick={() => {
                                            Analytics.go_to_selections()
                                        }}
                                    >
                                        Подборки
                                    </Link>
                                </MobileNavigation.Item>
                            </>
                        )}

                        {userAccount.is_hr && (
                            <>
                                <MobileNavigation.Line />
                                <MobileNavigation.Item>
                                    <Link to="/my/statistics">Личный кабинет HR</Link>
                                </MobileNavigation.Item>
                            </>
                        )}
                    </>
                ) : (
                    <>
                        <MobileNavigation.SubHeader>
                            <Link to="/survey/psy" className="button-secondary">
                                Подобрать психолога
                            </Link>
                        </MobileNavigation.SubHeader>
                        {!isClientAuthorizationPage() && (
                            <MobileNavigation.SubHeader>
                                <MyAuthorizationLink className="button-primary">
                                    Войти
                                </MyAuthorizationLink>
                            </MobileNavigation.SubHeader>
                        )}
                    </>
                )}
            </MobileNavigation.Highlight>
            {showClosedMenu && (
                <>
                    <MobileNavigation.Item>
                        <GiftLink />
                    </MobileNavigation.Item>
                    <MobileNavigation.Line />
                    <MobileNavigation.Item
                        className="mobile-navigation__item-dropdown"
                        onClick={() => {
                            setKnowledgeOpen(!knowledgeOpen)
                        }}
                    >
                        <button className="navigator-button">
                            Знания{" "}
                            {knowledgeOpen ? (
                                <Icon type="arrow-up-white" />
                            ) : (
                                <Icon type="arrow-down-white" />
                            )}
                        </button>
                    </MobileNavigation.Item>
                    <MobileNavigation.Line />
                    <MobileNavigation.Submenu hidden={!knowledgeOpen}>
                        <MobileNavigation.SubmenuItem>
                            <a href={`${APP_PAGE_URL}/guide`}>Гид по психотерапии</a>
                        </MobileNavigation.SubmenuItem>
                        <MobileNavigation.Line />
                        <MobileNavigation.SubmenuItem>
                            <a href={`${BLOG_URL}/`}>Блог</a>
                        </MobileNavigation.SubmenuItem>
                        <MobileNavigation.Line />
                        <MobileNavigation.SubmenuItem>
                            <a href={`${BLOG_URL}/likbez/chego-ozhidat-ot-psihoterapii/`}>
                                Что ожидать от терапии
                            </a>
                        </MobileNavigation.SubmenuItem>
                        <MobileNavigation.Line />
                        <MobileNavigation.SubmenuItem>
                            <a href={`${APP_PAGE_URL}/podcast`}>Подкасты Alter</a>
                        </MobileNavigation.SubmenuItem>
                        <MobileNavigation.Line />
                        <MobileNavigation.SubmenuItem>
                            <a href="https://stepik.org/course/60641/promo">
                                Онлайн-курс о терапии
                            </a>
                        </MobileNavigation.SubmenuItem>
                        <MobileNavigation.Line />
                    </MobileNavigation.Submenu>
                    <MobileNavigation.Item
                        className="mobile-navigation__item-dropdown"
                        onClick={() => {
                            setAboutOpen(!aboutOpen)
                        }}
                    >
                        <button className="navigator-button">
                            О нас{" "}
                            {aboutOpen ? (
                                <Icon type="arrow-up-white" />
                            ) : (
                                <Icon type="arrow-down-white" />
                            )}
                        </button>
                    </MobileNavigation.Item>
                    <MobileNavigation.Line />
                    <MobileNavigation.Submenu hidden={!aboutOpen}>
                        <MobileNavigation.SubmenuItem>
                            <a href={`${APP_PAGE_URL}/about`}>О сервисе</a>
                        </MobileNavigation.SubmenuItem>
                        <MobileNavigation.Line />
                        <MobileNavigation.SubmenuItem>
                            <a href={`${APP_PAGE_URL}/faq`}>Вопросы и ответы</a>
                        </MobileNavigation.SubmenuItem>
                        <MobileNavigation.Line />
                    </MobileNavigation.Submenu>
                    <MobileNavigation.Item className="mobile-navigation__item-is-light mt-3">
                        <Link to="/survey/psy">Подобрать психолога</Link>
                    </MobileNavigation.Item>
                    <MobileNavigation.Item className="mobile-navigation__item-is-light">
                        <Link to="/psychologists">Каталог психологов</Link>
                    </MobileNavigation.Item>
                    <MobileNavigation.Item className="mobile-navigation__item-is-light">
                        <a href={`${APP_PAGE_URL}/for-psychologists`}>Психологам</a>
                    </MobileNavigation.Item>
                    <MobileNavigation.Item className="mobile-navigation__item-is-light">
                        <a href={`${APP_PAGE_URL}/business`}>Бизнесу</a>
                    </MobileNavigation.Item>
                </>
            )}
        </MobileNavigation.Menu>
    )
}
